import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { buttonClasses } from "@/components/design/button-classes";
import { NoBackend } from "@/components/common/NoBackend";
import { GrowMediaSection } from "@/components/grow/GrowMediaSection";
import { PowerQuestionnaires } from "@/components/grow/PowerQuestionnaires";
import { GrowResourceDocument, PowersDataDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES, elementImageSrc } from "@/lib/elements";
import { elementDescription } from "@/lib/element-descriptions";
import { elementEvaluation } from "@/lib/element-evaluations";
import { POWER_BY_ABBR, TAG_LABEL_IDS } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { useOpenDocument } from "@/lib/use-open-document";

// Grow · Power detail (workshop snapshot screens/PowerDetail.jsx).
// The left aside is wired to REAL data: the element description (ported from
// discovery's i18n bundles) plus the Smart-Analyse result - score, Konfidenz,
// Begründung and the evidence list - and the Selbstbewertung score, both from
// PowersData. The right-hand content sections are still design-spec stubs.

// ELEMENT_CODES maps element → code; the route param is the code.
const ELEMENT_BY_CODE = new Map(Object.entries(ELEMENT_CODES).map(([el, code]) => [code, el]));

export function PowerDetailPage() {
  const { abbr = "" } = useParams();
  const intl = useIntl();
  const { data } = useQuery(PowersDataDocument);
  const [openError, setOpenError] = useState<string | null>(null);
  const openDocument = useOpenDocument(setOpenError);

  const element = ELEMENT_BY_CODE.get(abbr) ?? null;
  const { data: mediaData } = useQuery(GrowResourceDocument, {
    variables: { element: element as never },
    skip: !element,
  });
  const resource = mediaData?.growResource;
  const videos = (resource?.videos ?? []).map((v) => ({
    title: v.title.localized ?? "",
    durationMs: v.lengthMilliseconds,
    thumbnail: v.thumbnail,
  }));
  const podcasts = (resource?.audio ?? []).map((a) => ({
    title: a.title?.localized ?? "",
    durationMs: a.lengthMilliseconds,
    thumbnail: a.thumbnail,
  }));
  const scripts = (resource?.text ?? []).map((t) => ({
    title: t.title?.localized ?? "",
  }));

  const power = POWER_BY_ABBR[abbr];
  const powerNameText = power ? intl.formatMessage({ id: power.nameId }) : abbr;
  const description = element ? elementDescription(element, intl.locale) : null;
  const smart = element
    ? (data?.smartAnalysisResults ?? []).find((r) => String(r.element) === element)
    : undefined;
  const self = element
    ? (data?.selfAssessmentResults ?? []).find((r) => String(r.element) === element)
    : undefined;
  // Banded evaluation text for the self-assessment score, lives in i18n only
  // (see lib/element-evaluations) - previously shown only on the one-off
  // result screen right after finishing the self-assessment, not here on
  // repeat visits to the power's own page.
  const selfEvaluation = element ? elementEvaluation(element, self?.result, intl.locale) : null;
  const evidences = smart?.evidences ?? [];
  const imageSrc = element ? elementImageSrc(element) : null;

  // The power artwork is only as sharp as the self-assessment is complete:
  // blur tracks answered/total, mirroring the reveal on ElementAssessmentPage.
  const revealProgress =
    self && self.questionCount > 0 ? self.answeredQuestionCount / self.questionCount : 0;
  const revealBlur = 20 * (1 - revealProgress);

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). @adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@1: surface the SELF-assessment score only,
  // never the smart-analysis score/rationale/evidence shown on this page.
  useAssistantContext(
    element
      ? [
          {
            description: "power detail page the user is viewing",
            value:
              `power "${powerNameText}" (${abbr}). ` +
              (self
                ? `self-assessment score ${self.result ?? "unrated"}, ` +
                  `${self.answeredQuestionCount}/${self.questionCount} questions answered`
                : "not self-assessed yet") +
              " (self-assessment is the only power score available to you).",
          },
        ]
      : null,
  );

  // Guided tour (/tour): this power's page. @adr SPARK-0018-bff-proxies-agent-service-as-second-upstream@1: self-assessment status only — the tour
  // never mentions the smart-analysis score/rationale/evidence also shown here.
  useAssistantTour(
    element
      ? {
          label: intl.formatMessage({
            id: "grow.powerDetail.tour.label",
            defaultMessage: "Grow Power",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.powerDetail.tour.summary",
              defaultMessage:
                'This page is all about the power "{name}" ({abbr}). {status}',
            },
            {
              name: powerNameText,
              abbr,
              status:
                self && self.questionCount > 0
                  ? intl.formatMessage(
                      {
                        id: "grow.powerDetail.tour.summaryStatus",
                        defaultMessage:
                          "You have answered {answered} of {total} self-assessment statements.",
                      },
                      { answered: self.answeredQuestionCount, total: self.questionCount },
                    )
                  : intl.formatMessage({
                      id: "grow.powerDetail.tour.summaryStatusNone",
                      defaultMessage: "You haven't self-assessed it yet.",
                    }),
            },
          ),
          stops: [
            {
              anchor: "grow.power.artwork",
              title: intl.formatMessage({
                id: "grow.powerDetail.tour.artwork.title",
                defaultMessage: "The artwork reveal",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerDetail.tour.artwork.desc",
                  defaultMessage:
                    "The artwork is only as sharp as your self-assessment is complete. So far you have answered {answered} of {total} statements. Answer more to reveal it fully.",
                },
                {
                  answered: self?.answeredQuestionCount ?? 0,
                  total: self?.questionCount ?? 0,
                },
              ),
            },
            {
              anchor: "grow.power.description",
              title: intl.formatMessage({
                id: "grow.powerDetail.tour.description.title",
                defaultMessage: "What this power means",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerDetail.tour.description.desc",
                  defaultMessage:
                    "Read what \"{name}\" stands for and which cluster it belongs to. That is good grounding before you rate yourself.",
                },
                { name: powerNameText },
              ),
            },
            {
              anchor: "grow.power.selfEvaluation",
              title: intl.formatMessage({
                id: "grow.powerDetail.tour.selfEvaluation.title",
                defaultMessage: "Self-evaluation",
              }),
              description:
                self && self.questionCount > 0 && self.answeredQuestionCount === self.questionCount
                  ? intl.formatMessage(
                      {
                        id: "grow.powerDetail.tour.selfEvaluation.doneDesc",
                        defaultMessage:
                          "All {total} statements answered. Your self-assessment score is {score}. You can repeat the assessment anytime to update it.",
                      },
                      {
                        total: self.questionCount,
                        score: typeof self.result === "number" ? self.result : "–",
                      },
                    )
                  : intl.formatMessage(
                      {
                        id: "grow.powerDetail.tour.selfEvaluation.desc",
                        defaultMessage:
                          "Answer the statements about \"{name}\" here; {answered} of {total} are done. Completing all of them earns the medal and sharpens the artwork.",
                      },
                      {
                        name: powerNameText,
                        answered: self?.answeredQuestionCount ?? 0,
                        total: self?.questionCount ?? 0,
                      },
                    ),
            },
          ],
        }
      : null,
  );

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PowersData"
    >
      <div className="mb-5 flex items-center gap-3">
        <BackButton />
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage
            id="grow.powerDetail.eyebrow"
            defaultMessage="Power · {abbr}"
            values={{ abbr }}
          />
        </div>
      </div>

      <section className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]">
        <aside className="space-y-4">
          {imageSrc ? (
            <div
              className="box relative aspect-[4/3] w-full overflow-hidden"
              data-assistant-anchor="grow.power.artwork"
            >
              <img
                src={imageSrc}
                alt={powerNameText}
                className="absolute inset-0 h-full w-full scale-110 object-cover transition-all duration-700 ease-out"
                style={{ filter: `blur(${revealBlur}px)` }}
              />
              {revealProgress === 0 && element ? (
                <Link
                  to={`/grow/${element}`}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/40 text-center text-on-ink"
                >
                  <span className="font-serif text-lg leading-tight">
                    <FormattedMessage
                      id="grow.powerDetail.revealPower"
                      defaultMessage="Reveal “{name}”"
                      values={{ name: powerNameText }}
                    />
                  </span>
                  <span className={buttonClasses({ variant: "primary", size: "compact" })}>
                    <FormattedMessage
                      id="grow.powerDetail.startSelfAssessment"
                      defaultMessage="Start self-assessment"
                    />
                  </span>
                </Link>
              ) : null}
            </div>
          ) : null}
          <div data-assistant-anchor="grow.power.description">
            <h1 className="font-serif text-4xl leading-tight">{powerNameText}</h1>
            {power ? (
              <div className="field-label mt-1 text-ink/70">{intl.formatMessage({ id: TAG_LABEL_IDS[power.tag] })}</div>
            ) : null}
            {description ? (
              <p className="mt-3 text-sm leading-relaxed text-ink/80">{description}</p>
            ) : null}
          </div>

          {/* Smart-Analyse result card. bg-surface maps to --color-surface, the
              "Box" colour configurable in Settings (theme-prefs sets the CSS
              variable at runtime), so the card follows the user's theme. */}
          <div className="box-flat bg-surface px-4 py-5 text-center">
            <div className="field-label text-ink/70">
              <FormattedMessage id="grow.powerDetail.smartAnalysis" defaultMessage="Smart Analysis" />
            </div>
            <div className="mt-1 font-serif text-4xl leading-none tabular-nums">
              {typeof smart?.score === "number" ? smart.score : "–"}
            </div>
            <div className="field-label mt-1.5 italic text-ink/70">
              <FormattedMessage
                id="grow.powerDetail.confidence"
                defaultMessage="Confidence: {percent}%"
                values={{ percent: Math.round((smart?.confidence ?? 0) * 100) }}
              />
            </div>
          </div>

          <div className="box-flat bg-surface px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="field-label text-ink/70">
                <FormattedMessage id="grow.powerDetail.selfAssessment" defaultMessage="Self-assessment" />
              </span>
              <span className="font-serif text-xl leading-none tabular-nums">
                {typeof self?.result === "number" ? self.result : "–"}
              </span>
            </div>
            {selfEvaluation ? (
              <p className="mt-2 text-[12px] leading-relaxed text-ink/80">{selfEvaluation}</p>
            ) : null}
          </div>

          <div className="border-t border-ink/10 pt-3">
            <div className="field-label text-ink/70">
              <FormattedMessage id="grow.powerDetail.rationale" defaultMessage="Rationale" />
            </div>
            <p className="mt-1 text-[13px] leading-relaxed text-ink">
              {smart?.rationale ||
                intl.formatMessage({
                  id: "grow.powerDetail.noSmartAnalysis",
                  defaultMessage: "No smart analysis for this power yet.",
                })}
            </p>
          </div>

          <div className="border-t border-ink/10 pt-3">
            <div className="field-label text-ink/70">
              <FormattedMessage
                id="grow.powerDetail.evidence"
                defaultMessage="Evidence ({count})"
                values={{ count: evidences.length }}
              />
            </div>
            {evidences.length === 0 ? (
              <p className="mt-1 text-[12px] text-ink/55">
                <FormattedMessage
                  id="grow.powerDetail.noEvidence"
                  defaultMessage="No evidence available. Upload documents into the {link} to gather evidence."
                  values={{
                    link: (
                      <Link to="/save/basics" className="text-accent underline">
                        <FormattedMessage
                          id="grow.powerDetail.powerAnalysis"
                          defaultMessage="power analysis"
                        />
                      </Link>
                    ),
                  }}
                />
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {evidences.map((e, i) => (
                  <li key={i} className="box-flat bg-surface px-3 py-2">
                    <p className="text-[12px] italic leading-snug text-ink">„{e.quote}"</p>
                    <div className="field-label mt-1.5 flex flex-wrap items-center gap-x-2 text-ink/70">
                      {e.documentId ? (
                        <button
                          type="button"
                          className="truncate underline decoration-ink/30 underline-offset-2 transition hover:text-accent focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                          title={intl.formatMessage({ id: "grow.powerDetail.openDocument", defaultMessage: "Open document" })}
                          aria-label={intl.formatMessage(
                            { id: "grow.powerDetail.openDocumentNamed", defaultMessage: "Open document {name}" },
                            { name: e.documentName },
                          )}
                          onClick={() => {
                            setOpenError(null);
                            void openDocument(e.documentId!);
                          }}
                        >
                          {e.documentName}
                        </button>
                      ) : (
                        <span className="truncate">{e.documentName}</span>
                      )}
                      {/* Extraction liefert derzeit einen 0-basierten Index (0 = unbekannt/erste
                          Seite, priors immer 0); erst ab 1 ist die Zahl eine echte Seitenangabe. */}
                      {e.pageNumber != null && e.pageNumber > 0 ? (
                        <span>
                          ·{" "}
                          <FormattedMessage
                            id="grow.powerDetail.page"
                            defaultMessage="Page {number}"
                            values={{ number: e.pageNumber }}
                          />
                        </span>
                      ) : null}
                      {e.stance ? (
                        <span>
                          ·{" "}
                          {e.stance === "PRO" ? (
                            <FormattedMessage id="grow.powerDetail.stancePro" defaultMessage="Pro" />
                          ) : (
                            <FormattedMessage
                              id="grow.powerDetail.stanceContra"
                              defaultMessage="Contra"
                            />
                          )}
                        </span>
                      ) : null}
                      {e.support ? <span>· {String(e.support).toLowerCase()}</span> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {openError ? <p className="field-meta mt-2 text-danger">{openError}</p> : null}
          </div>
        </aside>

        <section className="space-y-6">
          {power ? (
            <div className="box bg-surface p-6">
              <div className="text-[10px] tracking-[0.25em] text-ink/55">
                <FormattedMessage id="grow.powerDetail.powerDetails" defaultMessage="Power details" />
              </div>
              {/* Prompt phrasing is randomized per click (askIntro/askImprove.0..9) for variety. */}
              <AskAssistantButton
                variant="secondary"
                className="mt-4"
                prompt={() =>
                  intl.formatMessage(
                    { id: `grow.powerDetail.askIntro.${Math.floor(Math.random() * 10)}` },
                    { name: powerNameText },
                  )
                }
                label={<FormattedMessage id="grow.powerDetail.askButton" values={{ name: powerNameText }} />}
              />
              <AskAssistantButton
                variant="secondary"
                className="ml-2 mt-4"
                prompt={() =>
                  intl.formatMessage(
                    { id: `grow.powerDetail.askImprove.${Math.floor(Math.random() * 10)}` },
                    { name: powerNameText },
                  )
                }
                label={<FormattedMessage id="grow.powerDetail.askImproveButton" values={{ name: powerNameText }} />}
              />
            </div>
          ) : null}
          {element ? (
            <div className="box bg-surface p-6" data-assistant-anchor="grow.power.selfEvaluation">
              <div className="text-[10px] tracking-[0.25em] text-ink/55">
                <FormattedMessage
                  id="grow.powerDetail.selfEvaluation"
                  defaultMessage="Self-evaluation"
                />
              </div>
              <p className="mt-2 text-sm text-ink/55">
                <FormattedMessage
                  id="grow.powerDetail.selfEvaluationIntro"
                  defaultMessage="Answer the statements about “{name}”. Your result feeds into the self-assessment score; once you have answered all of them you earn the medal."
                  values={{ name: powerNameText }}
                />
              </p>
              {self && self.questionCount > 0 ? (
                <div className="field-label mt-2 text-ink/55">
                  <FormattedMessage
                    id="grow.powerDetail.answeredCount"
                    defaultMessage="{answered}/{total} answered"
                    values={{ answered: self.answeredQuestionCount, total: self.questionCount }}
                  />
                </div>
              ) : null}
              <Link
                to={`/grow/${element}`}
                className={buttonClasses({ variant: "primary", size: "compact", className: "mt-4" })}
              >
                {self && self.questionCount > 0 && self.answeredQuestionCount === self.questionCount ? (
                  <FormattedMessage
                    id="grow.powerDetail.repeatSelfAssessment"
                    defaultMessage="Repeat self-assessment"
                  />
                ) : self && self.answeredQuestionCount > 0 ? (
                  <FormattedMessage
                    id="grow.powerDetail.continueSelfAssessment"
                    defaultMessage="Continue self-assessment"
                  />
                ) : (
                  <FormattedMessage
                    id="grow.powerDetail.startSelfAssessment"
                    defaultMessage="Start self-assessment"
                  />
                )}
              </Link>
            </div>
          ) : (
            <NoBackend
              label={intl.formatMessage({
                id: "grow.powerDetail.selfEvaluation",
                defaultMessage: "Self-evaluation",
              })}
              comingSoon
            />
          )}
          {element ? (
            <>
              <GrowMediaSection
                label={intl.formatMessage({
                  id: "grow.powerDetail.videos",
                  defaultMessage: "Videos",
                })}
                kind="Video"
                hue={210}
                variant="lines"
                items={videos}
                empty={intl.formatMessage(
                  {
                    id: "grow.powerDetail.noVideos",
                    defaultMessage: "No videos for “{name}” yet.",
                  },
                  { name: powerNameText },
                )}
              />
              <GrowMediaSection
                label={intl.formatMessage({
                  id: "grow.powerDetail.podcasts",
                  defaultMessage: "Podcasts",
                })}
                kind="Podcast"
                hue={280}
                variant="rings"
                items={podcasts}
                empty={intl.formatMessage(
                  {
                    id: "grow.powerDetail.noPodcasts",
                    defaultMessage: "No podcasts for “{name}” yet.",
                  },
                  { name: powerNameText },
                )}
              />
              <GrowMediaSection
                label={intl.formatMessage({
                  id: "grow.powerDetail.scripts",
                  defaultMessage: "Scripts",
                })}
                kind="Script"
                hue={40}
                variant="grid"
                items={scripts}
                empty={intl.formatMessage(
                  {
                    id: "grow.powerDetail.noScripts",
                    defaultMessage: "No scripts for “{name}” yet.",
                  },
                  { name: powerNameText },
                )}
              />
            </>
          ) : (
            <>
              <NoBackend
                label={intl.formatMessage({
                  id: "grow.powerDetail.videos",
                  defaultMessage: "Videos",
                })}
                comingSoon
              />
              <NoBackend
                label={intl.formatMessage({
                  id: "grow.powerDetail.podcasts",
                  defaultMessage: "Podcasts",
                })}
                comingSoon
              />
              <NoBackend
                label={intl.formatMessage({
                  id: "grow.powerDetail.scripts",
                  defaultMessage: "Scripts",
                })}
                comingSoon
              />
            </>
          )}
          {element ? (
            <PowerQuestionnaires element={element} abbr={abbr} powerName={powerNameText} />
          ) : (
            <NoBackend
              label={intl.formatMessage({
                id: "grow.powerDetail.questionnaires",
                defaultMessage: "Questionnaires",
              })}
              comingSoon
            />
          )}
        </section>
      </section>
    </main>
  );
}
